/**
 * The Query dialog's SQL as it is shown and copied (owner feedback 2026-09-28). dbt's compiled
 * SQL starts with the blank lines its Jinja blocks leave behind, and the executed text can carry a
 * block of them in the middle; neither says anything about the query.
 *
 * Pure and whitespace-only: blank (whitespace-only) lines at either end go, a run of blank lines
 * inside becomes one, and every line loses the leading whitespace ALL non-blank lines share (a
 * literal prefix, so a tab and spaces are never counted as the same). Within a line only the
 * leading indent can change, so a one-line string literal keeps its spaces. The text is not parsed:
 * a string literal that spans lines can lose its inner blank lines and its lines' indent. An
 * all-blank text normalises to "", which the dialog reports as an empty query.
 */

function isBlank(line: string): boolean {
  return line.trim() === "";
}

function leadingWhitespace(line: string): string {
  return /^[ \t]*/.exec(line)?.[0] ?? "";
}

function commonPrefix(a: string, b: string): string {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return a.slice(0, i);
}

/** The lines between the first and the last non-blank line. */
function trimBlankEnds(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && isBlank(lines[start] ?? "")) start += 1;
  while (end > start && isBlank(lines[end - 1] ?? "")) end -= 1;
  return lines.slice(start, end);
}

/** Every run of blank lines becomes one empty line. */
function collapseBlankRuns(lines: string[]): string[] {
  const kept: string[] = [];
  for (const line of lines) {
    if (!isBlank(line)) kept.push(line);
    else if (kept.at(-1) !== "") kept.push("");
  }
  return kept;
}

/** The leading whitespace every non-empty line shares, or null when there is no such line. */
function sharedIndent(lines: string[]): string | null {
  let indent: string | null = null;
  for (const line of lines) {
    if (line === "") continue;
    const lead = leadingWhitespace(line);
    indent = indent === null ? lead : commonPrefix(indent, lead);
    if (indent === "") break;
  }
  return indent;
}

export function normaliseSql(text: string): string {
  const kept = collapseBlankRuns(trimBlankEnds(text.split(/\r?\n/)));
  const cut = sharedIndent(kept)?.length ?? 0;
  return kept.map((line) => (line === "" ? line : line.slice(cut))).join("\n");
}
